import React, { useMemo, useState, useEffect, ReactNode } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { CATEGORY_CODE, PAGE, PAGE_CODE } from '../../../../lib/pageCodeDefaults';
import { Stack, ScrollArea, Group, Button, SegmentedControl, Text, Space } from '@mantine/core';
import BulkPricingPanelLoading from '../BulkPricingPanel/BulkPricingPanelLoading';
import SurveyJob from '../SurveyJob/SurveyJob';
import ViewSelector from '../../../ViewSelector/ViewSelector';
import Placeholder from '../../Placeholder/Placeholder';
import { elementShortNames } from '../../../../lib/constants';
import { searchInputSearch } from '../BulkPricingFacetPanel/BulkPricingFacetPanelReducer';
import { groupDataCutsBySurveyJob, getFieldData } from '../utils';
import { ISurveyJobPanelProps, ListView, IDataCutModifications } from './SurveyJobTypes';
import { areAllFacetsUnchecked } from '../../../../lib/facetHelpers';
import { DragLayer } from './DragLayer';
import { INode } from '../Types/BulkPricingTypes';
import { IAppState } from '../../../../lib/types';
import { useHistory } from 'react-router-dom';
import Pagination from 'ps-components/Pagination/Pagination';
import { bulkPricingActions } from '../BulkPricingFacetPanel/BulkPricingFacetPanelReducer';
import './SurveyJob.scss';

export default function SurveyJobPanel(props: ISurveyJobPanelProps) {
  const dispatch = useDispatch();
  const history = useHistory();
  const surveyState = useSelector((state: IAppState) => state.marketPricing.BulkPricing.SurveyLibrary);
  const facetPanelState = useSelector((state: IAppState) => state.marketPricing.BulkPricing.FacetPanel);
  const [listView, setListView] = useState<string>(ListView.ALL)

  const anyFacetsUnselected = areAllFacetsUnchecked(facetPanelState.facets, facetPanelState.facetSelection);
  const showResetAllButton = !anyFacetsUnselected || (facetPanelState.query && facetPanelState.query !== '');
  const { limit, offset } = surveyState.metadata;
  const { checkedDataCuts, setCheckedDataCuts } = props;

  const [dataCutModifications, setDataCutModifications] = useState<IDataCutModifications>({});

  const surveyJobsSearchResults = useMemo(() => {
    const surveyJobs = groupDataCutsBySurveyJob(surveyState.tableProps?.rowData, surveyState.terms)

    return surveyJobs.map((surveyJob) => {
      const modifiedDataCuts = dataCutModifications[surveyJob.id];
      if (modifiedDataCuts) return { ...surveyJob, children: modifiedDataCuts }
      else return surveyJob;
    })

  }, [surveyState.tableProps?.rowData, dataCutModifications] )

  const surveyJobsWithSelections = useMemo(() => groupDataCutsBySurveyJob(checkedDataCuts, surveyState.terms), [checkedDataCuts])
  const surveyJobs = listView === ListView.ALL ? surveyJobsSearchResults : surveyJobsWithSelections;

  useEffect(() => {
    history.replace({
      pathname: history.location.pathname,
      search: '',
    });
  }, [history.location.search]);

  const modifyDataCutList = (dataCuts: INode[], surveyJobId: string | number) => {
    //check if there are any checkedDataCuts that are not in the dataCuts list for the surveyJob. If so, uncheck them.
    setDataCutModifications({
      ...dataCutModifications,
      [surveyJobId]: dataCuts
    })
  }

  const onCheckDataCut = (currentDataCut: INode) => {
    if (checkedDataCuts.find((cut) => cut.id === currentDataCut.id)) {
      setCheckedDataCuts(checkedDataCuts.filter(cut => cut.id !== currentDataCut.id));
    } else {
      setCheckedDataCuts([...checkedDataCuts, currentDataCut]);
    }
  }

  const isChecked = (currentDataCut: INode) => Boolean(checkedDataCuts?.find(cut => cut.id === currentDataCut.id));

  const disableCheckbox = (currentDataCut: INode) => {
    const checkedDataCutCurrency = checkedDataCuts.length ? getFieldData('currency_default', checkedDataCuts[0].data, checkedDataCuts[0].terms) : null;
    const thisDataCutCurrency = getFieldData('currency_default', currentDataCut.data, currentDataCut.terms);
    return checkedDataCutCurrency ? thisDataCutCurrency !== checkedDataCutCurrency : false;
  }

  const showPlaceHolder = (surveyState?.data?.length === 0 || (surveyState?.data && surveyState?.data[0].length === 0)) && facetPanelState.loaded && !facetPanelState.loading;
  let primaryPlaceholderText: ReactNode = 'Survey job matches will appear here';
  let secondaryPlaceholderText: ReactNode  =
    'To see matches, use the search box to search by survey job info or start selecting filters on the left';

  if (facetPanelState.query && facetPanelState.query !== '') {
    primaryPlaceholderText = (
      <span>
        No search results for <b>“ {facetPanelState.query} ”</b>
      </span>
    );
    secondaryPlaceholderText = 'Please try different keywords or adjust your filters';
  } else if (!anyFacetsUnselected) {
    primaryPlaceholderText = 'No results found for set filters';
    secondaryPlaceholderText =
      'Please ensure you have identified default datacuts for these surveys in your survey library';
  }
  const noResultsPlaceholder = (
    <>
      <Space h={60}/>
      <Placeholder
        containerClassName="survey-library__no-results-container"
        primaryText={primaryPlaceholderText}
        secondaryText={secondaryPlaceholderText}
      />
    </>
  );

  const pageSurveyJobs = (limit: number, offset: number) => {
    dispatch(bulkPricingActions.pageList(limit, offset));
  }

  return (
    <Stack className='survey-job__panel bulk-pricing__panel '>
      <ScrollArea offsetScrollbars >
        <Stack gap='lg'>
          <Group justify="space-between">
            <ViewSelector
              label='Survey View'
              pageName='Survey Fields'
              pageCode={PAGE_CODE.BULK_PRICING_SURVEY_SEARCH}
              categoryCode={CATEGORY_CODE.BULK_PRICING_SURVEY_SEARCH}
              page={PAGE.BULK_PRICING_SURVEY_SEARCH}
              elementCountLimit={4}
              elementsFilter={(element: any, index: number, arr: any[]): boolean => {
                if (element.field_type_code !== 'monetary') return false;
                  const elementName = Object.values(elementShortNames[PAGE_CODE.BULK_PRICING_SURVEY_SEARCH])
                    .find(({ stem }) => {
                        return element?.text?.startsWith(stem)
                    })
                  return Boolean(elementName);
              }}
              categoryFilter={(category: any): boolean => (category?.text !== 'User Defined Formulas')}
              onChange={()=> {
                dispatch(searchInputSearch(facetPanelState.query));
                setDataCutModifications({});
              }}
            />
          </Group>
          <Group justify='space-between' gap='lg' h={40}>
            <Button
              className='remove-selected-link'
              variant='transparent'
              disabled={checkedDataCuts?.length === 0}
              onClick={() => { setCheckedDataCuts([])}}
              color='skyblue'
            >
              Clear All Selections
            </Button>
            <Text size='md' c={checkedDataCuts.length ? 'dark' : 'var(--mantine-color-ghost-9)'}>{checkedDataCuts.length || '0'} selected</Text>
            <SegmentedControl styles={{ label: { marginBottom: 0 }} } data={['All', 'Selected']} size="sm" onChange={setListView} mr={10}/>
          </Group>
          {surveyState.loading && <BulkPricingPanelLoading/>}
          {!surveyState.loading && (showPlaceHolder || !showResetAllButton) ? (
            noResultsPlaceholder
          ) : (
            <>
              <DragLayer />
              {surveyJobs.map((surveyJob) => (
                  <SurveyJob
                    key={surveyJob.id}
                    isSurveyPanel={true}
                    surveyJob={surveyJob}
                    checkedDataCuts={checkedDataCuts}
                    onDataCutListChange={(dataCutList: INode[]) => modifyDataCutList(dataCutList, surveyJob.id)}
                    onCheckDataCut={onCheckDataCut}
                    isChecked={isChecked}
                    disableCheckbox={disableCheckbox}
                    setCheckedDataCuts={setCheckedDataCuts}
                  />
                )
              )}
            </>
          )}
        </Stack>
      </ScrollArea>
      {showResetAllButton && (
        <Pagination
          className="survey-library__pagination"
          offset={offset}
          pageAction={pageSurveyJobs}
          pageShowRange={3}
          scrollTopPageChange
          shouldShowInput
          step={limit}
          totalCount={surveyState?.metadata?.totalCount && parseInt(surveyState?.metadata?.totalCount)}
        />
      )}

    </Stack>
  );
}
